import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  LineChart,
  Blocks,
  Code2,
  Workflow,
  Compass,
  Check,
} from 'lucide-react';
import HeroBanner from '../components/HeroBanner';
import { whatWeDoContent } from '../mock';

const iconMap = { LineChart, Blocks, Code2, Workflow, Compass };

const WhatWeDo = () => {
  return (
    <>
      <HeroBanner
        image={whatWeDoContent.heroImage}
        quote={whatWeDoContent.heroQuote}
        tall={false}
      />

      {/* Intro */}
      <section className="bg-[#e5dfe9] pt-24 pb-16 md:pt-32 md:pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-[12px] tracking-[0.28em] font-sans font-semibold text-neutral-600 mb-5">
            {whatWeDoContent.intro.eyebrow}
          </p>
          <h1
            className="font-serif italic text-neutral-800 mb-8 leading-tight"
            style={{ fontSize: 'clamp(2rem, 4vw, 2.9rem)' }}
          >
            {whatWeDoContent.intro.heading}
          </h1>
          <p
            className="text-neutral-700 font-sans"
            style={{ fontSize: '17.5px', lineHeight: '1.8' }}
          >
            {whatWeDoContent.intro.lead}
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-[#e5dfe9] pb-28">
        <div className="max-w-6xl mx-auto px-6 space-y-6">
          {whatWeDoContent.pillars.map((p, idx) => {
            const Icon = iconMap[p.icon] || Compass;
            return (
              <article
                key={p.key}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 bg-white/60 backdrop-blur-sm border border-neutral-400/25 rounded-sm p-8 md:p-12 hover:bg-white hover:border-neutral-500/50 transition-colors"
              >
                <div className="md:col-span-4 flex items-start gap-5">
                  <div className="shrink-0 w-14 h-14 rounded-full border border-neutral-500/50 flex items-center justify-center">
                    <Icon size={22} className="text-neutral-700" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-serif italic text-neutral-500 mb-1" style={{ fontSize: '1rem' }}>
                      {String(idx + 1).padStart(2, '0')}
                    </p>
                    <h2
                      className="font-sans font-semibold text-neutral-800 leading-tight"
                      style={{ fontSize: '22px' }}
                    >
                      {p.title}
                    </h2>
                  </div>
                </div>

                <div className="md:col-span-8">
                  <p
                    className="text-neutral-700 font-sans mb-6"
                    style={{ fontSize: '16.5px', lineHeight: '1.75' }}
                  >
                    {p.body}
                  </p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {p.capabilities.map((c, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2.5 text-neutral-700 font-sans"
                        style={{ fontSize: '15px', lineHeight: '1.55' }}
                      >
                        <Check
                          size={16}
                          className="text-neutral-500 mt-1 shrink-0"
                          strokeWidth={2}
                        />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#efeaf2] py-20 border-t border-neutral-300/40">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3
            className="font-serif italic text-neutral-800 mb-6"
            style={{ fontSize: 'clamp(1.6rem, 3vw, 2.1rem)' }}
          >
            Ready to build something enduring together?
          </h3>
          <p
            className="text-neutral-700 font-sans max-w-2xl mx-auto mb-10"
            style={{ fontSize: '17px', lineHeight: '1.75' }}
          >
            Tell us about the outcome you are pursuing. We will assemble the right practice mix, senior leadership and emerging practitioners for the engagement.
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 text-[13px] tracking-[0.22em] font-sans font-medium border border-neutral-700 text-neutral-800 rounded-full px-7 py-3 hover:bg-neutral-800 hover:text-white transition-colors"
          >
            START A CONVERSATION
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default WhatWeDo;
